import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { IconReplace } from '@tabler/icons-react'

import { Toggle } from '@/components/forms/Checkbox'
import {
  fetchOrganization,
  fetchOrganizationAuditEvents,
  updateOrganizationProperties,
} from '@/reducers/organizations/organization.actions'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { PageHeader } from '@/components/headers/PageHeader'
import { formatTime } from '@/utils/helpers'
import { Banner } from '@/components/banners/Banner'
import { Pill } from '@/components/pills/Pill'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'

export const OrganizationFlagsPage = () => {
  const dispatch = useDispatch()
  const organization = useSelector((state) => state.organization)
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const [loading, setLoading] = useState(false)
  const [auditEventsLoading, setAuditEventsLoading] = useState(false)
  const [confirmingOAuthOff, setConfirmingOAuthOff] = useState(false)
  const { organizationid: organizationId } = useParams()

  const reloadAuditHistory = () => {
    setAuditEventsLoading(true)
    dispatch(
      fetchOrganizationAuditEvents({
        organizationId,
        toggleLoading: () => setAuditEventsLoading(false),
      })
    )
  }

  useEffect(() => {
    if (currentlyOpenModalId === 'organizationFlags/audit') {
      reloadAuditHistory()
    }
  }, [currentlyOpenModalId])

  useEffect(() => {
    setLoading(true)
    dispatch(fetchOrganization({ organizationId, toggleLoading: () => setLoading(false) }))
  }, [])

  const handleUpdateFlag = (property) => {
    setLoading(true)
    dispatch(
      updateOrganizationProperties({
        organizationId: organization.id,
        reloadOrganization: fetchOrganization,
        [property]: !organization[property],
        toggleLoading: () => setLoading(false),
      })
    )
  }

  // Turning OAuth off revokes every grant in the org and can't be undone, so it asks first.
  const handleToggle = (property) => {
    if (property === 'oauth_enabled' && organization.oauth_enabled) {
      setConfirmingOAuthOff(true)
      return
    }

    handleUpdateFlag(property)
  }

  const handleConfirmOAuthOff = () => {
    setConfirmingOAuthOff(false)
    handleUpdateFlag('oauth_enabled')
  }

  const createToggle = (flag_name) => {
    return {
      as: (
        <Toggle
          data-testid={`${flag_name}-toggle`}
          aria-label={`Status: ${flag_name}`}
          checked={organization[flag_name]}
          onChange={() => handleToggle(flag_name)}
        />
      ),
      value: organization[flag_name],
    }
  }

  const getKeyAndActiveState = (flagName) => ({ key: flagName, active: createToggle(flagName) })

  const flags = [
    {
      ...getKeyAndActiveState('desktop_manual_config_selecting'),
      description:
        'Makes agents start calls by manually selecting from a list of playbooks in the desktop app. This is different than midcall playbook switching.',
    },
    {
      ...getKeyAndActiveState('save_audio'),
      description: 'Saves audio of calls for organization',
    },
    {
      ...getKeyAndActiveState('desktop_leaderboard_visibility'),
      description: 'Allows leaderboard visibility for organization',
    },
    {
      ...getKeyAndActiveState('qa_enabled'),
      description: 'Enables RTQA Scorecards for Automated Scorecards',
    },
    {
      ...getKeyAndActiveState('qa_copilot_enabled'),
      description:
        'Enables Quality. This is unnecessary for Coaching / Insights. However, Compliance requires this to be on.',
    },
    {
      ...getKeyAndActiveState('realtime_coaching_access'),
      description: 'Enables Realtime Coaching (now called Real-Time Alerts) for the organization',
    },
    {
      ...getKeyAndActiveState('desktop_enterprise_enabled'),
      description:
        'Enables Balto Enterprise for the organization (SPoG). After enabling this, you will need to toggle the feature for each user.',
    },
    {
      ...getKeyAndActiveState('custom_branding_enabled'),
      description: 'Enables custom branding for OEM partners',
    },
    {
      ...getKeyAndActiveState('novel_content_disabled'),
      // TODO: This looks unused, confirm and remove if so
      description: "Disables agent's ability to add checklist items",
    },
    {
      ...getKeyAndActiveState('content_customization_enabled'),
      description:
        'Enables the "Top Agent" feature for agents to customize checklist items on the org level',
    },
    {
      ...getKeyAndActiveState('screen_capture_access_enabled'),
      description:
        'Lets the org choose, per user, who has screen capture. Required before anyone can set the user-level toggle. For PCI orgs, only Balto security can turn this on.',
    },
    {
      ...getKeyAndActiveState('screen_capture_enabled'),
      description:
        'Enables screen capture (NOTE: Enabling this will turn it on for ALL users of the org). Turning this on also grants Screen Capture Access, so managers can still exclude individual users.',
    },
    {
      ...getKeyAndActiveState('qa_disputes_enabled'),
      description: 'Enables QA Disputes in Balto Cloud and the Agent app',
    },
    {
      ...getKeyAndActiveState('webhooks_enabled'),
      description:
        'Enables outbound customer webhooks. Users additionally need the Webhook Editor permission to register an endpoint.',
    },
    {
      ...getKeyAndActiveState('call_data_dump_enabled'),
      description:
        'Enables nightly call data dump job, and requires reaching out to #platform-team if a backfill is needed',
    },
    {
      ...getKeyAndActiveState('save_audio_after_postcall'),
      description: 'Enables saving the audio file after postcall is complete',
    },
    {
      ...getKeyAndActiveState('interval_call_data_dump_enabled'),
      description:
        'Enables call data dump job on shorter interval, must have call_data_dump_enabled on as well',
    },
    {
      ...getKeyAndActiveState('update_call_data_dump_enabled'),
      description:
        'Call data dump job will include calls updated on the date in addition to calls started on the date. (note: only some changes count as an "update" for data dump purposes)',
    },
    {
      ...getKeyAndActiveState('desktop_minimize_disabled'),
      description: 'Disables the minimize button in Agent app',
    },
    {
      ...getKeyAndActiveState('multi_call_scoring_enabled'),
      description:
        'Enables merging scores of multiple calls into a single score for RTQA and Quality',
    },
    {
      ...getKeyAndActiveState('agent_gpt_enabled'),
      description:
        'Enables AgentGPT for the organization. Each user must also be granted permission for them to use it.',
    },
    {
      ...getKeyAndActiveState('kodi_enabled'),
      description:
        "Sets the organization's default Kodi access. Turning this off disables Kodi for every user except those set to Enabled individually on the users page.",
    },
    {
      ...getKeyAndActiveState('oauth_enabled'),
      description:
        'Lets users connect OAuth apps, such as Kodi and Claude, that act as them in Balto. Turning this off disconnects every app in the organization at once; turning it back on reconnects none, so users connect them again.',
    },
    {
      ...getKeyAndActiveState('phrase_matching_intent_based_model_enabled'),
      description: 'Enables intent based phrase matching model',
    },
    {
      ...getKeyAndActiveState('show_start_button'),
      description: 'Show the start button on Agent app',
    },
    {
      ...getKeyAndActiveState('strict_checklist'),
      // TODO: Is this still used?
      description:
        'Enables strict checklist mode (higher fuzzy match threshold for checklist only & disables intent based phrase matching model)',
    },
    {
      ...getKeyAndActiveState('compliance_tasks_enabled'),
      description: 'Enables the Compliance Inbox. Quality needs to be enabled as well.',
    },
    {
      ...getKeyAndActiveState('coaching_tasks_enabled'),
      description: 'Enables the Coaching Inbox',
    },
    {
      ...getKeyAndActiveState('ringcentral_customer'),
      description:
        'Denotes whether the organization is a RingCentral customer. This is used to determine certain features that need to be turned off based on our agreement with RingCentral.',
    },
    {
      ...getKeyAndActiveState('use_smart_phrase_shadow_mode_model'),
      // TODO: Is this still used?
      description:
        'Whether to use the shadow mode smart phrases model (if exists) in place of the normal one.',
    },
    {
      ...getKeyAndActiveState('desktop_set_default_playbook'),
      description:
        'Enables agents to set a default playbook on the desktop app. Midcall playbook switching must be enabled for this to work.',
    },
    {
      ...getKeyAndActiveState('dynamic_checklist_enabled'),
      description:
        'Enables the ability to configure a dynamic checklist (NOTE: this requires additional setup from the integration).',
    },
    {
      ...getKeyAndActiveState('editable_notes_enabled'),
      description: 'Enables the ability to edit summary notes in the Agent app.',
    },
    {
      ...getKeyAndActiveState('session_timeout_enabled'),
      description: 'Enables automatic session timeouts for the organization.',
    },
    {
      ...getKeyAndActiveState('cid_specific_qaps_enabled'),
      description: 'Whether auto generated Q&A pairs for Agent GPT should be playbook specific',
    },
    {
      ...getKeyAndActiveState('dynamic_insights_enabled'),
      description: 'Enables dynamic insights for the organization.',
    },
    {
      ...getKeyAndActiveState('omnichannel_enabled'),
      description: 'Enables Omnichannel support for the organization.',
    },
    {
      ...getKeyAndActiveState('mfa_required'),
      description: 'Require multi-factor authentication for both cloud and desktop log ins',
    },
    {
      ...getKeyAndActiveState('desktop_automatic_device_switcher_enabled'),
      description:
        "Enables automatic device switching for the desktop app. This will automatically switch the users' microphone after 5 calls that are longer than a minute that have no valid transcription and switch the output device after 5 calls that are longer than 2 minutes.",
    },
    {
      ...getKeyAndActiveState('audio_ingestion_only'),
      description:
        'Enables audio ingestion only mode. This mode hides many features that are not currently implemented for audio ingestion only orgs.',
    },
    {
      ...getKeyAndActiveState('desktop_historical_call_recordings_enabled'),
      description:
        'Enables agents to access historical call recordings from the desktop app. Turning this off removes the audio player from the desktop app.',
    },
    {
      ...getKeyAndActiveState('ai_checklist_enabled'),
      description:
        'Enables AI Checklist for the organization, allowing users to add a prompt to a checklist item to evaluate.',
    },
    {
      ...getKeyAndActiveState('postcall_language_detection'),
      description: 'Enables Postcall Language Detection',
    },
    {
      ...getKeyAndActiveState('newrez_complaints_enabled'),
      description: 'ONLY FOR NEWREZ: Enables the ability to send complaint data to Newrez.',
    },
    {
      ...getKeyAndActiveState('outbound_campaigns_enabled'),
      description: 'Enables Outbound AI agent campaigns for the organization.',
    },
    {
      ...getKeyAndActiveState('togo_enabled'),
      description:
        'Enables Togo for the organization. Each user must also be granted the Togo Editor permission to edit Togo.',
    },
    {
      ...getKeyAndActiveState('tools_enabled'),
      description:
        'Enables Tools for the organization. Each user must also be granted the Tools Editor permission to edit Tools.',
    },
    {
      ...getKeyAndActiveState('alex_insights_enabled'),
      description: 'Enables Agentic Insights for the organization.',
    },
    {
      ...getKeyAndActiveState('voip_metadata_capture_enabled'),
      description:
        "Captures each call's VoIP metadata into fields that can be used in notes and filtered on in Call Explorer.",
    },
  ]

  const flagColumns = [
    {
      accessor: 'active',
      label: 'Status',
    },
    {
      accessor: 'key',
      label: 'Flag',
      isSearchable: true,
      format: (value) => <code>{value}</code>,
      isCopiable: true,
      collapsing: true,
    },
    {
      accessor: 'description',
      label: 'Description',
      isSearchable: true,
      format: (value) => <div className="break-spaces">{value}</div>,
    },
  ]

  const auditColumns = [
    {
      accessor: 'flag',
      label: 'What changed',
      isSearchable: true,
      format: (value) => <code>{value}</code>,
    },
    {
      accessor: 'value',
      label: 'Changed to',
      isSearchable: true,
      format: (value) => (value === 'On' ? <Pill success>On</Pill> : <Pill critical>Off</Pill>),
    },
    {
      accessor: 'author',
      label: 'Who changed it',
      isSearchable: true,
    },
    {
      accessor: 'timestamp',
      label: 'When it happened',
      isSearchable: true,
    },
  ]

  const formatBooleanToOnOff = (value) => {
    return value ? 'On' : 'Off'
  }

  const formatAuditRows = (auditEvents) => {
    return auditEvents.map((event) => {
      return {
        key: event.id,
        flag: Object.keys(event.event_payload)[0],
        value: formatBooleanToOnOff(Object.values(event.event_payload)[0]),
        author: event.author,
        timestamp: formatTime(event.created_at),
      }
    })
  }

  return (
    <>
      <PageHeader
        title={`Organization Flags / ${organization.name}`}
        data-testid="header-name"
        actions={
          <ButtonAndFormModal
            buttonLabel="Change History"
            icon={<IconReplace />}
            buttonProps={{ loading: auditEventsLoading }}
            modalId="organizationFlags/audit"
            modalProps={{ size: 'large' }}
            modalTitle="Change History"
            modalContent={
              <div className="modal-content-wrapper">
                <Card className="no-padding table-segment">
                  <AdvancedTable
                    rows={formatAuditRows(organization.auditEvents || [])}
                    columns={auditColumns}
                    defaultOrderBy="key"
                    defaultOrder="desc"
                    index="key"
                    pagination
                    testid="audit-events-table"
                    loading={auditEventsLoading}
                  />
                </Card>
              </div>
            }
          />
        }
      />
      <Banner informative>
        Please make sure you know what you are doing before changing any of these flags. Ask
        Engineering for help if you are unsure. Thank you!
      </Banner>
      <Card className="no-padding">
        <AdvancedTable
          loading={loading}
          rows={flags}
          columns={flagColumns}
          defaultOrderBy="key"
          index="key"
        />
      </Card>
      <ConfirmModal
        show={confirmingOAuthOff}
        onClose={() => setConfirmingOAuthOff(false)}
        title="Turn off connected apps?"
        confirmLabel="Turn off"
        destructive
        onConfirm={handleConfirmOAuthOff}
        testIdPrefix="oauth-off"
      >
        <p>
          Every OAuth app connected in {organization.name}, such as Kodi and Claude, is disconnected
          right away. Turning this back on reconnects none of them, so users connect each app again.
        </p>
      </ConfirmModal>
    </>
  )
}

export default OrganizationFlagsPage
